/** "Your rides suggest…" (0.9.12): once enough rides are in, what they show (max HR, an LTHR estimate, the usual
 * week, indoor months, the trainer app) next to what the rider has now. Shown until applied or kept. */
import { useState } from "react";
import { useApplySuggestions, useDismissSuggestions, useSuggestions } from "@/api/hooks";
import type { Suggestion } from "@/api/schemas";
import { useToast } from "./Toast";
import { Card } from "./ui";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const APPS: Record<string, string> = { mywhoosh: "MyWhoosh", zwift: "Zwift", rouvy: "Rouvy", other: "Other" };

const months = (xs: number[]) => (xs.length ? xs.map((m) => MONTHS[m - 1] ?? m).join(", ") : "none");
const hrs = (h: number) => `${Number.isInteger(h) ? h : h.toFixed(1)} h`;

/** One line per suggestion: what the rides show, then what the rider has now. */
export function describeSuggestion(x: Suggestion): { value: string; now: string } {
  switch (x.field) {
    case "max_hr":
      return {
        value: `${x.value} bpm`,
        now: x.current_typed ? `you entered ${x.current}` : `now ${x.current}`,
      };
    case "lthr":
      return { value: `${x.value} bpm, estimated from max HR`, now: `now ${x.current}` };
    case "week":
      return {
        value: `about ${hrs(x.value_hours)}, rides ${x.ride_days.map((d) => DAYS[d]).join(", ")}`,
        now: `now ${hrs(x.current_hours)}`,
      };
    case "indoor_months":
      return { value: months(x.value), now: `now ${months(x.current)}` };
    case "trainer_app":
      return {
        value: APPS[x.value] ?? x.value,
        now: `now ${x.current ? (APPS[x.current] ?? x.current) : "none"}`,
      };
  }
}

export function RideSuggestions() {
  const q = useSuggestions();
  const apply = useApplySuggestions();
  const dismiss = useDismissSuggestions();
  const toast = useToast();
  const [off, setOff] = useState<Set<string>>(new Set());
  const items = q.data?.items ?? [];
  if (!items.length) return null;
  const chosen = items.map((x) => x.field).filter((f) => !off.has(f));
  const toggle = (f: string) =>
    setOff((s) => {
      const n = new Set(s);
      if (n.has(f)) n.delete(f);
      else n.add(f);
      return n;
    });
  const b = q.data!.based_on;
  const busy = apply.isPending || dismiss.isPending;
  return (
    <Card title="Your rides suggest">
      <p className="muted" style={{ marginTop: 0 }}>
        From {b.rides} rides{b.weeks ? ` over ${b.weeks} weeks` : ""}. Pick what to use: the plan is rebuilt
        with it, and your own changes to sessions stay.
      </p>
      <ul className="plain suggestions" data-testid="ride-suggestions">
        {items.map((x) => {
          const d = describeSuggestion(x);
          return (
            <li key={x.field}>
              <label className="check">
                <input type="checkbox" checked={!off.has(x.field)} onChange={() => toggle(x.field)} />
                <span>
                  <b>{x.label}:</b> {d.value} <span className="muted">({d.now})</span>
                </span>
              </label>
            </li>
          );
        })}
      </ul>
      <div className="row">
        <button
          className="primary"
          disabled={busy || chosen.length === 0}
          onClick={() =>
            apply.mutate(chosen, {
              onSuccess: () => toast.ok("Plan updated with what your rides show"),
              onError: (e) => toast.error(e),
            })
          }
        >
          {apply.isPending
            ? "Updating the plan…"
            : chosen.length === items.length
              ? "Use all"
              : "Use selected"}
        </button>
        <button disabled={busy} onClick={() => dismiss.mutate(undefined, { onError: (e) => toast.error(e) })}>
          Keep mine
        </button>
      </div>
    </Card>
  );
}
